import React, { useEffect, useState } from 'react';
import { Box, TextInput } from 'grommet';
import { API } from '../api';
import QueryString from 'qs';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

export const SearchBar = () => {
    const navigate = useNavigate();
    const [searchTerms, setSearchTerms] = useState('');
    const [searchResults, setSearchResults] = useState();

    const debounced = useDebouncedCallback((value) => {
        searchRequest(value);
    }, 400);

    useEffect(() => {
        if (!searchTerms.length) {
            setSearchResults();
            return;
        }
        debounced(searchTerms);
    }, [searchTerms]);

    const searchRequest = async (searchString) => {
        const companyResults = await searchCompanies(searchString);
        const contactResults = await searchContacts(searchString);
        const jobResults = await searchJobs(searchString);
        const schoolResults = await searchSchools(searchString);
        const pipelineResults = await searchPipelines(searchString);

        const fullResults = companyResults
            .concat(contactResults, jobResults, schoolResults, pipelineResults)
            .sort((resultObj1, resultObj2) => {
                if (resultObj1.label < resultObj2.label) return -1;
                else if (resultObj1.label > resultObj2.label) return 1;
                else return 0;
            });
        setSearchResults(fullResults);
    };

    const handleSelect = (toUrl) => {
        setSearchTerms('');
        setSearchResults([]);
        navigate(toUrl);
    };

    return (
        <Box width="medium" align="center" round="6px" background="white">
            <TextInput
                dropHeight="large"
                onChange={(event) => setSearchTerms(event.target.value)}
                onSuggestionSelect={(event) =>
                    handleSelect(event.suggestion.value)
                }
                placeholder="Search"
                suggestions={searchResults || []}
                value={searchTerms}
            />
        </Box>
    );
};

const searchCompanies = async (searchString) => {
    try {
        const query = QueryString.stringify({
            filters: {
                name: { $containsi: searchString },
            },
            pagination: {
                limit: 10,
            },
            sort: ['name'],
        });

        const companiesResponse = await API.get(`/api/companies?${query}`);
        const formattedCompanies = companiesResponse.data.data.map(
            (company) => {
                return {
                    label: `${company.attributes.name}`,
                    value: `/companies/${company.id}`,
                };
            },
        );

        return formattedCompanies;
    } catch (error) {
        console.log(error);
        return [];
    }
};

const searchContactsByName = async (searchString) => {
    try {
        const firstNameSearchString = searchString.includes(' ')
            ? searchString.split(' ')[0]
            : searchString;
        const lastNameSearchString = searchString.includes(' ')
            ? searchString.split(' ')[1]
            : searchString;
        const query = QueryString.stringify({
            filters: {
                $or: [
                    {
                        $and: [
                            {
                                firstName: {
                                    $containsi: firstNameSearchString,
                                },
                            },
                            { lastName: { $containsi: lastNameSearchString } },
                        ],
                    },
                    { firstName: { $containsi: searchString } },
                    { lastName: { $containsi: searchString } },
                    { email: { $containsi: searchString } },
                ],
            },
            pagination: {
                limit: 10,
            },
            sort: ['firstName', 'lastName'],
        });

        const contactsResponse = await API.get(`/api/contacts?${query}`);
        const formattedContacts = contactsResponse.data.data.map((contact) => {
            return {
                label: `${contact.attributes.firstName} ${contact.attributes.lastName}`,
                value: `/contacts/${contact.id}`,
            };
        });

        return formattedContacts;
    } catch (error) {
        console.log(error);
        return [];
    }
};

const MIN_PHONE_SEARCH_DIGITS = 4;

const searchContactsByPhone = async (searchString) => {
    const digits = searchString.replace(/\D/g, '');
    if (digits.length < MIN_PHONE_SEARCH_DIGITS) return [];

    try {
        const query = QueryString.stringify({ q: digits });
        const response = await API.get(`/api/contacts/search/phone?${query}`);
        return response.data.data.map((contact) => ({
            label: `${contact.firstName} ${contact.lastName} ${contact.phoneNumber}`,
            value: `/contacts/${contact.id}`,
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};

// Phone matches come first so their richer label wins on duplicates.
export const searchContacts = async (searchString) => {
    const [phoneResults, nameResults] = await Promise.all([
        searchContactsByPhone(searchString),
        searchContactsByName(searchString),
    ]);

    const seen = new Set();
    return phoneResults.concat(nameResults).filter(({ value }) => {
        if (seen.has(value)) return false;
        seen.add(value);
        return true;
    });
};

const searchJobs = async (searchString) => {
    const query = QueryString.stringify({
        filters: {
            $or: [
                { title: { $containsi: searchString } },
                // { company: { name: { $containsi: searchString }}},
            ],
        },
        pagination: {
            limit: 10,
        },
        populate: ['company'],
        sort: ['title'],
    });

    try {
        const jobsResponse = await API.get(`/api/jobs?${query}`);
        const formattedJobs = jobsResponse.data.data.map((job) => {
            return {
                label: `${job.attributes.title} [${job.attributes.company.data.attributes.name}]`,
                value: `/jobs/${job.id}`,
            };
        });
        return formattedJobs;
    } catch (error) {
        console.log(error);
        return [];
    }
};

const searchSchools = async (searchString) => {
    const query = QueryString.stringify({
        filters: {
            name: { $containsi: searchString },
        },
        pagination: {
            limit: 5,
        },
        sort: ['name'],
    });

    try {
        const schoolsResponse = await API.get(`/api/schools?${query}`);
        const formattedSchools = schoolsResponse.data.data.map((school) => {
            return {
                label: `${school.attributes.name}`,
                value: `/schools/${school.id}`,
            };
        });
        return formattedSchools;
    } catch (error) {
        console.log(error);
        return [];
    }
};

const searchPipelines = async (searchString) => {
    const query = QueryString.stringify({
        filters: { name: { $containsi: searchString } },
        pagination: { limit: 10 },
        sort: ['name:asc'],
    });

    try {
        const pipelinesResponse = await API.get(`/api/pipelines?${query}`);

        return pipelinesResponse.data.data.map((x) => ({
            label: x.attributes.name,
            value: `/pipelines/${x.id}`,
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};
